import { formatDollars } from '@/helpers/money'

import {
  PRODUCT_COLORS,
  REVENUE_SERIES,
  revenueChartData,
  revenuePeriodKey,
  revenuePeriodLabel
} from './reportParser.service'

const OTHER_COLOR = 'rgb(156, 163, 175)'
const MS_PER_DAY = 24 * 60 * 60 * 1000
const MAX_DAY_RANGE = 31
const MAX_WEEK_RANGE = 120

const CENT_FIELDS = ['gross_cents', 'refunds_cents', 'net_cents', 'orders']

const emptyFigures = () => Object.fromEntries(CENT_FIELDS.map(field => [field, 0]))

const addFigures = (target, source) => {
  CENT_FIELDS.forEach(field => {
    target[field] += Number(source[field]) || 0
  })
  return target
}

const toDollars = cents => cents / 100

export const inclusiveDays = (beginningDate, endingDate) => {
  const begin = new Date(beginningDate + 'T12:00:00')
  const end = new Date(endingDate + 'T12:00:00')
  return Math.round((end - begin) / MS_PER_DAY) + 1
}

export function groupByForRange(beginningDate, endingDate) {
  const days = inclusiveDays(beginningDate, endingDate)
  if (days <= MAX_DAY_RANGE) return 'day'
  if (days <= MAX_WEEK_RANGE) return 'week'
  return 'month'
}

const isoDay = ms => new Date(ms).toISOString().slice(0, 10)

// Every calendar day from..to inclusive as YYYY-MM-DD, walked in UTC so DST never skips or doubles a day.
const daysInRange = ({ from, to }) => {
  const days = []
  const last = Date.parse(`${to}T00:00:00Z`)
  for (let ms = Date.parse(`${from}T00:00:00Z`); ms <= last; ms += MS_PER_DAY) days.push(isoDay(ms))
  return days
}

const isoDate = /^\d{4}-\d{2}-\d{2}$/
const validRange = range => !!range && isoDate.test(range.from) && isoDate.test(range.to)

// The sales endpoint omits zero rows, so the periods come from the requested range, not from the rows.
const rangePeriods = (range, groupBy) => {
  const labels = new Map()
  daysInRange(range).forEach(day => {
    const key = revenuePeriodKey(day, groupBy)
    if (key !== null && !labels.has(key)) labels.set(key, revenuePeriodLabel(key, groupBy))
  })
  return labels
}

const withinRange = (day, range) => day >= range.from && day <= range.to

/** range: { from, to } YYYY-MM-DD. Without a range the periods come from the rows alone. */
export function bucketFranchiseRows(rows, groupBy, range = null) {
  const ranged = validRange(range)
  const labels = ranged ? rangePeriods(range, groupBy) : new Map()
  const bySeries = {}
  const totals = {}
  if (ranged) labels.forEach((_label, key) => (totals[key] = emptyFigures()))
  ;(rows || []).forEach(row => {
    if (ranged && !withinRange(row.day, range)) return
    const key = revenuePeriodKey(row.day, groupBy)
    if (key === null) return
    if (!labels.has(key)) labels.set(key, revenuePeriodLabel(key, groupBy))
    const seriesBuckets = (bySeries[row.series_id] = bySeries[row.series_id] ?? {})
    addFigures((seriesBuckets[key] = seriesBuckets[key] ?? emptyFigures()), row)
    addFigures((totals[key] = totals[key] ?? emptyFigures()), row)
  })
  const periods = [...labels.keys()].sort().map(key => ({ key, label: labels.get(key) }))
  return { periods, bySeries, totals }
}

const periodRevenue = (period, totals) => {
  const figures = totals[period.key] ?? emptyFigures()
  return {
    label: period.label,
    gross: toDollars(figures.gross_cents),
    refunds: toDollars(figures.refunds_cents),
    net: toDollars(figures.net_cents)
  }
}

export function salesOverTimeChartData({ periods, totals }) {
  return revenueChartData(
    periods.map(period => periodRevenue(period, totals)),
    Object.keys(REVENUE_SERIES)
  )
}

const seriesLabel = option => (option.location ? `${option.name} (${option.location})` : option.name)

const netCentsByPeriod = (periods, buckets) => periods.map(period => buckets?.[period.key]?.net_cents ?? 0)

const sum = values => values.reduce((total, value) => total + value, 0)

const seriesEntry = (option, periods, bySeries) => {
  const cents = netCentsByPeriod(periods, bySeries[option.id])
  return { seriesId: option.id, label: seriesLabel(option), cents, totalCents: sum(cents) }
}

const fallbackOption = (seriesId, seriesKind) => ({
  id: seriesId,
  name: `${seriesKind === 'store' ? 'Store' : 'Location'} ${seriesId}`,
  location: null
})

// A series id with sales but no option still ranks, under a fallback label, so the chart matches the tiles.
const rankedSeries = (seriesOptions, periods, bySeries, seriesKind) => {
  const known = new Set(seriesOptions.map(option => String(option.id)))
  const unknown = Object.keys(bySeries)
    .filter(id => !known.has(id))
    .map(id => fallbackOption(id, seriesKind))
  return [...seriesOptions, ...unknown]
    .filter(option => bySeries[option.id])
    .map(option => seriesEntry(option, periods, bySeries))
    .sort((a, b) => b.totalCents - a.totalCents || a.label.localeCompare(b.label))
}

const withOther = (ranked, top, periodCount) => {
  if (ranked.length <= top) return ranked
  const rest = ranked.slice(top)
  const cents = Array.from({ length: periodCount }, (_, i) => sum(rest.map(entry => entry.cents[i])))
  const other = { seriesId: 'other', label: 'Other', cents, totalCents: sum(cents), color: OTHER_COLOR }
  return [...ranked.slice(0, top), other]
}

/** seriesOptions: [{ id, name, location }] from the sales endpoint's series_options. */
export function salesByLocationChartData(
  { periods, bySeries },
  seriesOptions,
  { top = 5, seriesKind = 'merchant' } = {}
) {
  const entries = withOther(rankedSeries(seriesOptions || [], periods, bySeries, seriesKind), top, periods.length).map(
    (entry, index) => ({ ...entry, color: entry.color ?? PRODUCT_COLORS[index % PRODUCT_COLORS.length] })
  )
  const chartData = {
    labels: periods.map(period => period.label),
    datasets: entries.map(entry => ({
      label: entry.label,
      data: entry.cents.map(toDollars),
      backgroundColor: entry.color,
      borderColor: entry.color,
      yAxisID: 'y',
      seriesId: entry.seriesId
    }))
  }
  const tableData = {
    columns: periods.map(period => ({ key: period.key, label: period.label })),
    rows: entries.map(entry => ({
      label: entry.label,
      color: entry.color,
      values: Object.fromEntries(periods.map((period, i) => [period.key, formatDollars(toDollars(entry.cents[i]))])),
      total: formatDollars(toDollars(entry.totalCents)),
      seriesId: entry.seriesId
    }))
  }
  return { chartData, tableData }
}

export const fractionChange = (current, previous) => (previous ? (current - previous) / previous : null)

const TILES = [
  { key: 'gross', label: 'Gross sales', field: 'gross_cents', money: true },
  { key: 'net', label: 'Net sales', field: 'net_cents', money: true },
  { key: 'orders', label: 'Orders', field: 'orders' },
  { key: 'aov', label: 'Average order value', field: 'aov_cents', money: true },
  { key: 'activeLocations', label: 'Active locations', field: 'active_locations' },
  { key: 'customers', label: 'Customers', field: 'customers' }
]

export function tileFigures({ totals, previous_totals: previous }) {
  return TILES.map(({ key, label, field, money }) => {
    const current = Number(totals?.[field]) || 0
    const before = Number(previous?.[field]) || 0
    return {
      key,
      label,
      value: money ? formatDollars(toDollars(current)) : current,
      delta: fractionChange(current, before)
    }
  })
}
